import * as THREE from 'three';
import gsap from 'gsap';

export class HeartParticles {
    public points: THREE.Points;
    private geometry: THREE.BufferGeometry;
    private originalPositions: Float32Array;
    private material: THREE.PointsMaterial;
    private time: number = 0;
    private isExploding: boolean = false;

    constructor() {
        this.geometry = new THREE.BufferGeometry();
        const particleCount = 3000;

        const positions = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        const randoms = new Float32Array(particleCount * 3); // For fuzzy volume

        const scale = 0.5;

        for (let i = 0; i < particleCount; i++) {
            // Heart shape parametric equations
            // t goes from 0 to 2PI
            // We can distribute t randomly or evenly
            const t = Math.random() * Math.PI * 2;

            // Rejection sampling or just layers for volume
            // Let's use layers: r is "radius" or "scale" of this specific point from center
            // But parametric surface is better. 
            // Let's just use the 2D parametric curve and extrude/fuzz it in Z.

            // x = 16sin^3(t)
            // y = 13cos(t) - 5cos(2t) - 2cos(3t) - cos(4t)

            let x = 16 * Math.pow(Math.sin(t), 3);
            let y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
            let z = (Math.random() - 0.5) * 4; // Thickness

            // Fuzziness - scatter points inside/outside slightly to make it look like a cloud
            const fuzz = 1 + (Math.random() - 0.5) * 0.4;
            x *= fuzz;
            y *= fuzz;
            z *= Math.random() * 2; // More thickness

            // Scale down
            x *= scale;
            y *= scale;
            z *= scale;

            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;

            sizes[i] = Math.random() * 0.3 + 0.1;

            randoms[i * 3] = (Math.random() - 0.5);
            randoms[i * 3 + 1] = (Math.random() - 0.5);
            randoms[i * 3 + 2] = (Math.random() - 0.5);
        }

        this.originalPositions = positions.slice();

        this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        // Stashing original positions in userData is risky for buffers, better keep reference or attribute
        // We'll update the 'position' attribute directly from originalPositions every frame during pulse

        // Custom texture could be good, but simple circle is fine
        // Let's make a soft sprite
        // const sprite = this.createTexture();

        this.material = new THREE.PointsMaterial({
            color: 0xff3366, // Darker pink for visibility
            size: 0.35, // Even bigger
            sizeAttenuation: true,
            vertexColors: true,
            transparent: true,
            opacity: 0.9,
            depthWrite: false,
            blending: THREE.NormalBlending // Normal blending for light background
        });

        this.points = new THREE.Points(this.geometry, this.material);
        this.points.position.y = 5; // Float high
    }



    update(delta: number) {
        if (this.isExploding) return; // Handled by simple physics/gsap if exploding

        this.time += delta * 2;

        // Pulse animation
        const scale = 1 + Math.sin(this.time) * 0.05;
        this.points.scale.set(scale, scale, scale);

        // Gentle rotation
        this.points.rotation.y += delta * 0.2;
    }

    async explode(): Promise<void> {
        this.isExploding = true;
        const positions = this.geometry.attributes.position.array as Float32Array;
        const count = positions.length / 3;

        // Animate particles outward
        // We need to animate each vertex. This is heavy for GSAP to handle 3000 individual tweens.
        // Better to use a simpler loop or a shader.
        // For simplicity, let's use a "physics" update in the main loop or just a quick dispersal here.

        // Let's create an object to tween
        const explosion = { progress: 0 };

        return new Promise(resolve => {
            gsap.to(explosion, {
                progress: 1,
                duration: 1.5,
                ease: 'power2.out',
                onUpdate: () => {
                    const prog = explosion.progress;
                    for (let i = 0; i < count; i++) {
                        // Original pos
                        const ox = this.originalPositions[i * 3];
                        const oy = this.originalPositions[i * 3 + 1];
                        const oz = this.originalPositions[i * 3 + 2];

                        // Direction is roughly away from center (0,0,0 local)
                        // Actually, let's just use the normalized vector of the position
                        const len = Math.sqrt(ox * ox + oy * oy + oz * oz) || 0.01;
                        const dirX = ox / len;
                        const dirY = oy / len;
                        const dirZ = oz / len;

                        // Move outward
                        // Add some randomness
                        const dist = 50 * prog; // Fly 50 units away

                        positions[i * 3] = ox + dirX * dist + (Math.random() - 0.5) * prog * 5;
                        positions[i * 3 + 1] = oy + dirY * dist + (Math.random() - 0.5) * prog * 5;
                        positions[i * 3 + 2] = oz + dirZ * dist + (Math.random() - 0.5) * prog * 5;
                    }
                    this.geometry.attributes.position.needsUpdate = true;
                    // Fade out
                    this.material.opacity = 1 - prog;
                },
                onComplete: () => {
                    this.points.visible = false;
                    resolve();
                }
            });
        });
    }
}
